<template>
	<BaseLoading v-if="isLoading" is-full-page />
	<div
		v-else
		class="main-wrapper-large"
	>
		<!-- Print Layout Bill -->
		<div
			v-if="canUpdateOrderStatus"
			class="container-print"
		>
			<iframe ref="iframe"></iframe>
		</div>
		<!-- End Print Layout Bill -->

		<!-- General information -->
		<CRow>
			<CCol md="6">
				<h2 class="typo-h4 mb-0 d-flex align-items-center">
					Order ID #{{ order.orderCode }}
					<CBadge
						:color="orderStatus.color"
						class="badge-order-status ml-3"
						data-test-id="badge-order-status"
					>
						{{ orderStatus.title }}
					</CBadge>
				</h2>
			</CCol>
			<CCol md="6" class="d-flex align-items-center justify-content-end text-right">
				<CButton
					:disabled="reconciledButtonDisabled || isReconcile"
					color="light"
					class="btn-new-user transparent"
					@click="UpdateReconcileStatus"
				>
					<template v-if="!isReconcile">
						Reconcile
					</template>
					<template v-else>
						<CIcon name="cil-check-alt" class="mr-2" />
						<span>
							{{ $t('button.reconciled') }}
						</span>
					</template>
				</CButton>
				<CButton
					v-if="canUpdateOrderStatus"
					:disabled="readyToPickButtonDisabled"
					color="light"
					class="btn-new-user transparent mr-3 ml-3"
					@click="isShowModalConfirmUpdateStatus = true"
				>
					Ready to pick
				</CButton>
				<CButton
					v-if="canUpdateOrderStatus"
					:disabled="printButtonDisabled"
					color="light"
					class="btn-new-user transparent"
					@click="print"
				>
					<template v-if="isLoadingDoc">
						Loading...
					</template>
					<template v-else>
						<span class="mr-2">
							{{ $t('button.print') }}
						</span>
						<CIcon name="cil-print" />
					</template>
				</CButton>
			</CCol>
		</CRow>
		<CRow class="summary mt-3">
			<CCol md="12">
				<p class="typo-body-2 m-0 p-0">
					Order type:
					<span class="color-black-45">
						{{ order.type || '-' }}
					</span>
				</p>
			</CCol>
			<CCol md="12">
				<p class="typo-body-2 m-0 p-0">
					Order ID (from channel):
					<span class="color-black-45">
						#{{ order.externalOrderNumber }}
					</span>
				</p>
			</CCol>
			<CCol md="12">
				<p class="typo-body-2 m-0 p-0">
					Placed from: <span class="color-black-45">{{ order.store.name }}</span><span class="date color-black-45">{{ order.createdAt }}</span>
				</p>
			</CCol>
			<CCol
				v-if="isReconcile"
				md="12"
			>
				<p class="typo-body-2">
					Reconciled at: <span class="color-black-45">{{ order.commitments.syncedWithITECAt }}</span>
				</p>
			</CCol>
		</CRow>
		<CRow class="customer">
			<CCol md="12">
				<h3 class="typo-h5 mt-3">
					Customer
				</h3>
				<p class="typo-h6 mb-1">
					{{ `${order.customer.firstName} ${order.customer.lastName}` }}
				</p>
				<div class="d-inline-block">
					<label class="typo-label color-black-45">ID: <span class="typo-body-2 font-weight-normal color-black">{{ order.customer.id }}</span></label>
					<label class="typo-label color-black-45 ml-4">Customer group: <span class="typo-body-2 font-weight-normal color-black">{{ order.customer.customerGroup.name }}</span></label>
					<label class="typo-label color-black-45 ml-4">Phone: <span class="typo-body-2 font-weight-normal color-black">{{ order.customer.phoneNumber }}</span></label>
					<label class="typo-label color-black-45 ml-4">Email: <span class="typo-body-2 font-weight-normal color-black">{{ order.customer.email }}</span></label>
				</div>
			</CCol>
		</CRow>
		<!-- End General information -->

		<!-- Table -->
		<CRow>
			<CCol>
				<div
					class="product-table mt-4"
					data-test-id="product-table"
				>
					<BaseTable
						:fields="ORDER_DETAIL_TABLE_FIELDS"
						:items="dataTable"
						:hide-columns="['id']"
						class="table-custom"
					>
						<template #no-items-view>
							<div class="empty-table-element">
								<p class="subtitle">
									{{ $t("message.not_found_order_list") }}
								</p>
							</div>
						</template>
						<template #product="{item}">
							<div class="product-info">
								<img
									:src="item.thumbnail"
									class="thumbnail"
								>
								<div class="description">
									<p class="mb-0">
										{{ item.name }}
									</p>
									<p class="color-black-45 mb-0">
										SKU: {{ item.sku }}
									</p>
								</div>
							</div>
						</template>
					</BaseTable>
					<div class="product-table-summary pt-2" data-test-id="product-table-summary">
						<div class="row">
							<label class="col typo-label">Total item</label>
							<span class="col typo-body-2 text-right">{{ order.quantity }}</span>
						</div>
						<div class="row">
							<label class="col typo-label">Total Discount</label>
							<span class="col typo-body-2 text-right">-{{ order.discount }}</span>
						</div>
						<div class="row">
							<label class="col typo-label">Subtotal</label>
							<span class="col typo-body-2 text-right">{{ order.subtotal }}</span>
						</div>
						<div class="row">
							<label class="col typo-label">Shipping Fee (Standard)</label>
							<span class="col typo-body-2 text-right">{{ order.shippingFee }}</span>
						</div>
						<!-- TODO: Recheck with BE for what actual key of response -->
						<div v-if="order.additionalSippingFee" class="row">
							<label class="col typo-label">Additional Shipping Fee</label>
							<span class="col typo-body-2 text-right">{{ order.additionalSippingFee }}</span>
						</div>
						<div class="row total-price">
							<label class="col typo-label mb-0 pl-0">Total price</label>
							<span class="col typo-body-2 text-right mb-0 pr-0">{{ order.total }}</span>
						</div>
					</div>
				</div>
			</CCol>
		</CRow>
		<!-- End Table -->

		<!-- Modal Confirm Update -->
		<ModalConfirmUpdateOrderStatus
			ref="modal-confirm-update-status"
			:show.sync="isShowModalConfirmUpdateStatus"
			:title="$t('message.do_you_want_to_update_order_status')"
			:disabled-submit="isUpdatingOrderStatus"
			@onSubmit="handleConfirmUpdateStatus"
			@onCancel="handleCancelUpdateStatus"
		/>
		<!-- End Modal Confirm Update -->
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { ORDER_COLOR, ORDER_TITLE, ORDER_DETAIL_TABLE_FIELDS, ORDER_STATUS, ORDER_ITEM_TYPE, ORDER_ITEM_TYPE_TITLE } from '../enums/order';
import { SHIPMENT_STATUS } from '../enums/shipment';

export default {
	name: 'OrderSingle',

	data() {
		return {
			orderId: this.$route.params.id,
			ORDER_DETAIL_TABLE_FIELDS,
			ORDER_ITEM_TYPE,
			ORDER_ITEM_TYPE_TITLE,
			isShowModalConfirmUpdateStatus: false,
			isDisabledPrint: false,
		};
	},

	computed: {
		...mapState('orderSingle', {
			order: 'data',
			isLoading: 'isLoading',
		}),
		...mapState('orderDoc', {
			doc: 'data',
			errorDoc: 'error',
			isLoadingDoc: 'isLoading',
		}),
		...mapState('orderStatusUpdate', {
			errorUpdateOrderStatus: 'error',
			isUpdatingOrderStatus: 'isLoading',
		}),
		...mapGetters('me', {
			isUser: 'isUser',
		}),
		...mapState('orderDocMultiple', {
			isLoadingDocMultiple: 'isLoading',
			errorDocMultiple: 'error',
			dataDocMultiple: 'data',
		}),
		orderStatus() {
			return {
				title: ORDER_TITLE.ORDER[this.order.orderStatus],
				color: ORDER_COLOR.ORDER[this.order.orderStatus],
			};
		},
		dataTable() {
			return this.order && this.order.items ? this.order.items.map((item) => ({
				id: item.id,
				product: {
					name: item.name,
					sku: item.sku,
					thumbnail: item.thumbnail,
				},
				priceSelling: item.priceSelling,
				quantity: item.quantity,
				discount: `-${item.discount}`,
				total: item.total,
			})) : [];
		},
		isPreraring() {
			return this.order.orderStatus === ORDER_STATUS.PREPARING;
		},
		isBookCourier() {
			return this.order.shippingStatus === SHIPMENT_STATUS.READY_TO_BOOK_COURIER;
		},
		isReconcile() {
			return this.order.commitments.syncedWithITECAt;
		},
		printButtonDisabled() {
			if (this.isUser || this.isLoadingDocMultiple || (this.isPreraring && this.isBookCourier)) {
				return true;
			}

			return ![ORDER_STATUS.PREPARING, ORDER_STATUS.SHIPPED].includes(this.order.orderStatus);
		},
		readyToPickButtonDisabled() {
			if (this.isUser || (this.isPreraring && !this.isBookCourier)) {
				return true;
			}

			return ![ORDER_STATUS.PREPARING].includes(this.order.orderStatus);
		},
		isChannelLazada() {
			const name = this.order?.store?.name || '';
			return name.toString().toLowerCase().trim() === 'lazada';
		},
		isChannelShopee() {
			const name = this.order?.store?.name || '';
			return name.toString().toLowerCase().trim() === 'shopee';
		},
		canUpdateOrderStatus() {
			return this.isChannelLazada || this.isChannelShopee;
		},
		reconciledButtonDisabled() {
			if (this.order.orderStatus === ORDER_STATUS.PREPARING
			&& this.order.shippingStatus === SHIPMENT_STATUS.READY_TO_BOOK_COURIER) {
				return true;
			}
			return this.order.orderStatus === ORDER_STATUS.CANCELED || this.order.orderStatus === ORDER_STATUS.EXPIRED;
		},
	},

	async created() {
		await this.fetchData();

		this.setPageTitle({
			title: `Order #${this.order.orderCode}`,
			isShow: true,
		});
	},

	methods: {
		...mapActions({
			setPageTitle: 'pageTitle/setPageTitle',
		}),
		...mapActions('orderDoc', {
			getOrderDoc: 'getOrderDoc',
		}),
		...mapActions('orderStatusUpdate', {
			updateOrderStatus: 'updateOrderStatus',
		}),
		...mapActions('orderDocMultiple', {
			getOrderDocMultiple: 'getOrderDocMultiple',
		}),
		...mapActions('orderSingle', {
			updateOrderReconcileStatus: 'updateOrderReconcileStatus',
		}),
		...mapActions('toast', {
			showToast: 'showToast',
		}),
		async fetchData() {
			await this.$store.dispatch('orderSingle/getOrder', { id: this.$route.params.id });
		},
		async print() {
			await this.getOrderDocMultiple({
				order_ids: [this.order.id],
			});

			if (this.errorDocMultiple) {
				this.showToast({
					header: this.$t('toast.unexpectedError'),
					content: this.errorDocMultiple.message,
					type: 'danger',
				});
			} else {
				window.open(this.dataDocMultiple.link, '_blank');
			}
		},
		async handleConfirmUpdateStatus() {
			await this.updateOrderStatus(this.$route.params.id);

			const error = this.errorUpdateOrderStatus !== null;

			if (!error) {
				this.isShowModalConfirmUpdateStatus = false;
				this.showToast({
					header: this.$t('toast.success'),
					content: this.$t('toast.update_order_status'),
					type: 'success',
				});
			} else {
				this.showToast({
					header: this.$t('toast.unexpectedError'),
					content: this.errorUpdateOrderStatus.message,
					type: 'danger',
				});
			}
		},
		handleCancelUpdateStatus() {
			this.isShowModalConfirmUpdateStatus = false;
		},
		async UpdateReconcileStatus() {
			await this.updateOrderReconcileStatus(this.$route.params.id);

			const error = this.errorUpdateOrderStatus !== null;

			if (!error) {
				this.showToast({
					header: this.$t('toast.success'),
					content: this.$t('toast.updaterOrderReconciledStatus'),
					type: 'success',
				});
			} else {
				this.showToast({
					header: this.$t('toast.unexpectedError'),
					content: this.errorUpdateOrderStatus.message,
					type: 'danger',
				});
			}
		},
	},
};
</script>

<style lang="scss" scoped>
	.summary {
		.date {
			&::before {
				content: "|";
				display: inline-block;
				margin-left: rem(5);
				margin-right: rem(5);
			}
		}
	}

	.customer {
		&-note {
			border-radius: rem(4);
			min-height: rem(72);
			padding: rem(16);

			background-color: $color-gray-100;
		}
	}

	.badge-order-status {
		padding: rem(7) rem(8);
		font-size: rem(14);
	}

	.product-table {
		border: rem(1) solid $color-gray-300;
		border-radius: rem(4);

		.product-cel {
			.product-info {
				display: flex;

				.thumbnail {
					flex: 0 0 rem(40);
					width: rem(40);
					height: rem(40);

					border-radius: rem(4);
					border: rem(1) solid $color-gray-300;
				}

				.description {
					flex: 1;
					margin-left: rem(16);
				}
			}
		}

		&-summary {
			max-width: rem(350);
			margin: 0 rem(16) rem(24) auto;

			.total-price {
				border-top: rem(1) solid $color-gray-300;
				border-bottom: rem(3) solid $color-black;

				margin: rem(16) 0 0 0;
				padding: rem(16) 0;
			}
		}

		.empty-table-element {
			margin: 0;
		}
	}

	::v-deep .table-custom {
		margin: 0;
		overflow-x: scroll;
		overflow-y: visible;
		border-radius: rem(4);
		text-align: right;

		thead > tr > th:first-child,
		tbody > tr > td:first-child {
			padding-left: rem(16);
		}

		thead > tr > th:last-child,
		tbody > tr > td:last-child {
			padding-right: rem(16);
		}

		thead {
			th {
				padding: rem(12) rem(4);
				background-color: $color-gray-100;
				width: 12%;
			}
		}

		tbody {
			td {
				padding: rem(12) rem(4);
				word-break: unset;
			}
		}

		.product {
			&-col, &-cel {
				text-align: left;
				word-break: break-word;
			}
		}

		.quantity {
			&-col, &-cel {
				text-align: center;
				word-break: break-word;
				min-width: rem(70);
			}
		}

		// Overrid column width
		.product-col {
			width: 32%;
		}

		.quantity-col {
			width: 7%;
		}

		.total-col {
			width: auto;
		}
	}

	.container-print {
		display: none;
	}
</style>
